import styled from "@emotion/styled";
import { Tooltip } from "@mui/material";
import { useCallback, type ComponentProps, type PropsWithChildren, type ReactNode } from "react";

import { colors } from "@skilitics/designer-theme";

type State = "on" | "off";

const transition = "150ms ease-in-out";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;

  height: 100%;

  padding-block-start: 3px;
`;

const Button = styled.button<{ $state: State }>`
  display: flex;
  justify-content: center;
  align-items: center;

  width: 39px;
  height: 39px;

  color: ${({ $state, theme }) => ($state === "on" ? theme.primaryStrong : theme.generalTextIcon)};
  background-color: ${({ theme, $state }) => ($state === "on" ? theme.primaryContainer : "transparent")};

  border: none;
  border-radius: ${({ theme }) => `${theme.borderRadius}px`};
  padding: 0;

  &:disabled {
    color: ${colors.uiGrey["250"]};
  }

  &:focus {
    outline: none;
  }

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.primaryStrong};

    ${({ $state, theme }) => $state === "off" && `background-color: ${theme.outlineSoft};`}

    cursor: pointer;
  }

  transition:
    color ${transition},
    background-color ${transition};
`;

const Bar = styled.div`
  height: 3px;
  width: 0;

  background-color: ${({ theme }) => theme.primaryStrong};

  border-radius: ${({ theme }) => theme.borderRadius}px;

  opacity: 0;

  &.on {
    width: calc(100% - 12px);
    opacity: 1;
  }

  transition:
    width ${transition},
    opacity ${transition};
`;

type Props = ComponentProps<"button"> & {
  state: State;
  tooltip?: ReactNode;
  onOn?: () => void;
  onOff?: () => void;
  onToggle?: () => void;
  "data-test": string;
};

export const IndicatorButton = ({
  state,
  tooltip,
  onOn,
  onOff,
  onToggle,
  children,
  ...props
}: PropsWithChildren<Props>) => {
  const handleClick = useCallback(() => {
    if (state === "on") {
      onOff?.();
    } else {
      onOn?.();
    }
    onToggle?.();
  }, [onOff, onOn, onToggle, state]);

  return (
    <Tooltip title={tooltip} placement="top">
      <Container>
        <Button onClick={handleClick} $state={state} {...props}>
          {children}
        </Button>
        <Bar className={state} />
      </Container>
    </Tooltip>
  );
};
